/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import {
    object, bool, string, func, number, shape, arrayOf,
} from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import withStyles from '@material-ui/core/styles/withStyles';

import BackButton from '../../common/BackButton';
import Loading from '../../common/Loading';
import { isEmpty } from '../../utils/object';
import EventDispatcher from '../../state/reducers/EventDispatcher';
import { getFeatureFlags } from '../../utils/featureSelectors';
import getSiteConfig from '../../utils/getSiteConfig';
import DesktopOrderDetailsByEmail from '../DesktopOrderDetails/DesktopOrderDetailsByEmail';
import DesktopOrderList from '../DesktopOrdersList/DesktopOrderList';
import ROSSDesktopOrderList from '../DesktopOrdersList/ROSSDesktopOrderList';
import MobileOrderList from '../MobileOrderList/MobileOrderList';
import ROSSMobileOrderList from '../MobileOrderList/ROSSMobileOrderList';
import { OrderDetailsStyle } from '../../style/OrderDetailsStyle';
import { determineLanguagePrefixPath } from '../../../../helpers/determineInternationalizationPath';
import configENV from '../../config';

import { getBrand } from '../../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import { getDataDictionary, getSSRDeviceType } from '../../../../../state/ducks/App/App-Selectors';
import { getLanguageName } from '../../../../../state/ducks/Member/ducks/Auth/helper/helper';

const { PERSIST_KEY } = configENV;

const styles = ({ typography, palette }) => OrderDetailsStyle(typography, palette);

class OrderDetails extends React.Component {
    // eslint-disable-next-line camelcase
    UNSAFE_componentWillMount = () => {
        const {
            match: {
                params: {
                    uuid, order, email,
                },
            },
            featureFlags,
            ssrDeviceType,
            fetchOrdersByEmail,
        } = this.props;

        // after successfully validated recipient and billing zip code
        // eslint-disable-next-line object-curly-newline
        const options = order ? { order, email } : null;

        if (
            uuid
            && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(uuid)
        ) {
            fetchOrdersByEmail(
                uuid,
                options,
                featureFlags,
                ssrDeviceType,
            );
        }
    };

    componentDidMount = () => {
        if (typeof window !== 'undefined') {
            window.scrollTo(0, 0);
        }
    };

    getOrderItemList = (uuid, order, brand, mobile) => {
        const { userProfile } = this.props;
        const orderItem = {
            uuid,
            orderNumber: order.orderNumber,
            orderDate: order.orderDate,
            items: [{ ...order }],
            className: '',
        };

        return mobile ? <MobileOrderList key={order.intOrderNumber} order={orderItem} className="" userProfile={userProfile} brand={brand} />
            : <DesktopOrderList key={order.intOrderNumber} order={orderItem} className="" userProfile={userProfile} brand={brand} />;
    }

    getROSSOrderItemList = (order, brand, mobile) => {
        const orderItem = [{ ...order }];
        return mobile ? <ROSSMobileOrderList order={orderItem} brand={brand} /> : <ROSSDesktopOrderList order={orderItem} brand={brand} />;
    };

    getOrderNumber = (orderDetailById) => {
        const { featureFlags, ROSSOrder } = this.props;
        const isROSSEnabled = featureFlags?.['is-order-page-using-ross-endpoint'];
        let orderNumber = '';
        if (isROSSEnabled) {
            orderNumber = ROSSOrder?.orderID;
        } else {
            orderNumber = orderDetailById?.[0]?.orderNumberPure || orderDetailById?.[0]?.orderNumber;
        }
        return orderNumber;
    }

    errorScreen = () => {
        const { dataDictionary } = this.props;
        return (
            <div className="orderDetails">
                <h1>
                    <BackButton url={determineLanguagePrefixPath('/track-order')} />
                    {getLanguageName() === '/en' ? 'Order Details' : dataDictionary?.['Order Details'] || 'Order Details'}
                </h1>
                <div className="error-inline">
                    {getLanguageName() === '/en' ? 'Unable to obtain order details for this order' : dataDictionary?.['Unable to obtain order details for this order'] || 'Unable to obtain order details for this order'}
                </div>
            </div>
        );
    }

    nonAuthenticatedScreen = () => (
        <div>
            <div className="margin-bottom-4px">
                <span className="non-auth-title">For the customer:</span>To view your complete order history, or to make modifications to your order, please sign-in to your account for more information.
            </div>
            <div>
                <span className="non-auth-title">For the recipient:</span>If you need more information, or you have questions or concerns regarding your gift, please contact customer service at {getSiteConfig('phoneNumber')}.
            </div>
        </div>
    )

    print = () => {
        if (typeof window !== 'undefined') {
            window.print();
        }
    }

    render() {
        const {
            match: {
                params: { uuid },
            },
            orderDetails,
            userProfile: { isAuthenticated },
            isFetchingModal,
            featureFlags,
            history,
            brand,
            requestStatus,
            orderDataLoaded,
            ROSSOrder,
        } = this.props;

        const orderIndex = uuid;
        const isROSSEnabled = featureFlags?.['is-order-page-using-ross-endpoint'];

        // Loading screen
        if (isFetchingModal || !orderDataLoaded) {
            return <Loading />;
        }
        if (!isROSSEnabled && ((isEmpty(orderDetails) && requestStatus === 200) || requestStatus === 400 || !uuid)) {
            return this.errorScreen();
        }

        // Redirect to /account/orders if orderDetails[orderIndex] is empty
        // Redirect to /track-order if orderDetails[orderIndex] is empty and the brand is 08F
        const orderDetailById = orderDetails[orderIndex];
        if (!isROSSEnabled && (!orderDetailById || orderDetailById.length < 1)) {
            if (!isAuthenticated && (brand.code === '08F' || brand.code === 'CAN')) {
                history.replace(determineLanguagePrefixPath('/track-order'));
            } else if (!isAuthenticated) {
                history.replace('/account/orders');
            }
            return this.errorScreen();
        }

        const orderNumber = this.getOrderNumber(orderDetailById);

        return <DesktopOrderDetailsByEmail orderDetails={ROSSOrder} orderNumber={orderNumber} />;
    }
}

OrderDetails.defaultProps = {
    match: {},
    history: {},
    orderDetails: {},
    featureFlags: {},
    email: null,
    ROSSOrder: {},
};

OrderDetails.propTypes = {
    history: object,
    isFetchingModal: bool.isRequired,
    userProfile: shape({
        accessToken: string,
        idToken: string,
        ContactId: string,
        userType: string,
        userRole: string,
        email: string,
        aud: string,
        iat: number,
        exp: number,
        sub: string,
        firstName: string,
        lastName: string,
        loginsCount: number,
        isAuthenticated: bool,
        isGuestAccount: bool,
    }).isRequired,
    match: object,
    orderDetails: object, // TODO: to validate propType object with dynamic key
    featureFlags: object,
    fetchOrdersByEmail: func.isRequired,
    email: string,
    brand: object.isRequired,
    dataDictionary: object.isRequired,
    ROSSOrder: shape({
        brandId: string,
        modifiedDateTime: string,
        orderNumber: string,
        orders: arrayOf({
            actualDeliveryDate: string,
            address: string,
            carrier: string,
            courier: string,
            createdDate: string,
            currentLocation: object,
            deliveryDate: string,
            deliveryImageURL: string,
            deliveryMethod: string,
            deliveryNotes: string,
            history: arrayOf({
                codeStatus: string,
                createdDate: string,
                location: object,
                status: string,
                statusDescription: string,
            }),
            intOrderNumber: string,
        }),
        items: arrayOf({
            productBrand: string,
            itemType: string,
            productName: string,
            productSku: string,
        }),
        origin: string,
        recipientFirstName: string,
        recipientLastName: string,
        signatureImageURL: string,
        status: string,
        statusDescription: string,
        trackingNumber: string,
        type: string,
        warningMessage: string,
    }),
    ssrDeviceType: string.isRequired,
    requestStatus: number.isRequired,
    orderDataLoaded: bool.isRequired,
};

const mapStateToProps = (state) => {
    /* istanbul ignore next */
    const newState = state[PERSIST_KEY] ? state[PERSIST_KEY] : state;
    let featureFlags = {};
    if (state.app) {
        try {
            featureFlags = {
                ...getFeatureFlags(state),
            };
            // eslint-disable-next-line no-empty
        } catch (ex) { }
    }
    return {
        isFetchingModal: newState.isFetchingModal,
        callBack: newState.callBack,
        orderNumber: newState.orderNumber,
        zipCode: newState.zipCode,
        billingZipCode: newState.billingZipCode,
        uuid: newState.uuid,
        orderDetails: newState.orderDetails,
        featureFlags,
        brand: getBrand(state),
        dataDictionary: getDataDictionary(state),
        ROSSOrder: newState.ROSSOrder,
        ssrDeviceType: getSSRDeviceType(state),
        requestStatus: newState.requestStatus,
        orderDataLoaded: newState.orderDataLoaded,
    };
};

const mapDispatchToProps = (dispatch) => ({
    fetchOrdersByEmail: (uuid, options, featureFlags, ssrDeviceType) => dispatch(EventDispatcher.handleSubmitOrderByEmail({
        uuid,
        options,
        featureFlags,
        ssrDeviceType,
    })),
});

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(withRouter(withStyles(styles, { withTheme: true })(OrderDetails)));
